import React from 'react';
import { 
  FolderKanban, 
  CheckCircle2, 
  Users, 
  TrendingUp, 
  Zap, 
  Clock, 
  ArrowUpRight, 
  AlertCircle,
  Plus,
  Bot
} from 'lucide-react';

export default function Dashboard({ projects, tasks, team, setActiveTab, setCurrentProjectId, onOpenNewTask, onOpenNewProject }) {
  const completedTasks = tasks.filter(t => t.status === 'done').length;
  const totalTasks = tasks.length;
  const completionRate = totalTasks ? Math.round((completedTasks / totalTasks) * 100) : 0;
  
  const urgentTasks = tasks.filter(t => t.priority === 'Urgent' && t.status !== 'done');
  const activeProjectsCount = projects.filter(p => p.status === 'In Progress').length;

  return (
    <div className="p-8 space-y-8 animate-fadeIn">
      
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-indigo-900 via-indigo-800 to-violet-900 rounded-3xl p-8 text-white shadow-xl relative overflow-hidden flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="absolute -right-10 -bottom-10 w-64 h-64 bg-indigo-500/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="space-y-2 relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-700/60 rounded-full text-xs font-medium text-indigo-200">
            <Zap className="w-3.5 h-3.5 text-yellow-400" /> AI-Driven Workflow Active
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">Good day, Elena 👋</h1>
          <p className="text-indigo-200 text-sm max-w-xl">
            Your teams are running <span className="font-semibold text-white">4 active sprints</span> with 92% velocity efficiency. 3 urgent security tasks require your attention.
          </p>
        </div>
        <div className="flex items-center space-x-3 relative z-10">
          <button 
            onClick={onOpenNewTask}
            className="px-5 py-2.5 bg-white text-indigo-900 hover:bg-indigo-50 rounded-xl font-semibold text-sm transition shadow-lg flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> New Task
          </button>
          <button 
            onClick={() => setActiveTab('ai')}
            className="px-5 py-2.5 bg-indigo-600/60 hover:bg-indigo-600 text-white rounded-xl font-semibold text-sm transition border border-indigo-400/30 flex items-center gap-2"
          >
            <Bot className="w-4 h-4 text-indigo-200" /> AI Copilot
          </button>
        </div>
      </div>

      {/* KPI Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Active Projects</span>
            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
              <FolderKanban className="w-5 h-5" />
            </div>
          </div>
          <div className="flex items-baseline justify-between">
            <h3 className="text-3xl font-extrabold text-slate-900">{activeProjectsCount}</h3>
            <span className="text-xs font-semibold text-emerald-600 flex items-center gap-0.5">
              <TrendingUp className="w-3.5 h-3.5" /> +2 this month
            </span>
          </div>
          <p className="text-xs text-slate-500">Total budget allocated: $180,000</p>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Task Completion</span>
            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-xl">
              <CheckCircle2 className="w-5 h-5" />
            </div>
          </div>
          <div className="flex items-baseline justify-between">
            <h3 className="text-3xl font-extrabold text-slate-900">{completionRate}%</h3>
            <span className="text-xs font-semibold text-emerald-600">
              {completedTasks}/{totalTasks} done
            </span>
          </div>
          <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
            <div className="bg-emerald-500 h-full rounded-full" style={{ width: `${completionRate}%` }}></div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Team Capacity</span>
            <div className="p-2 bg-violet-50 text-violet-600 rounded-xl">
              <Users className="w-5 h-5" />
            </div>
          </div>
          <div className="flex items-baseline justify-between">
            <h3 className="text-3xl font-extrabold text-slate-900">77%</h3>
            <span className="text-xs font-semibold text-indigo-600">Optimal Load</span>
          </div>
          <p className="text-xs text-slate-500">5 members active across departments</p>
        </div>

        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">AI Automations</span>
            <div className="p-2 bg-amber-50 text-amber-600 rounded-xl">
              <Bot className="w-5 h-5" />
            </div>
          </div>
          <div className="flex items-baseline justify-between">
            <h3 className="text-3xl font-extrabold text-slate-900">192</h3>
            <span className="text-xs font-semibold text-emerald-600 flex items-center gap-0.5">
              <TrendingUp className="w-3.5 h-3.5" /> 38 hrs saved
            </span>
          </div>
          <p className="text-xs text-slate-500">3 active workflows running</p>
        </div>
      </div>

      {/* Main Grid: Projects & Urgent Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Projects Progress Section (2 cols) */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Active Projects Overview</h2>
              <p className="text-xs text-slate-500">Real-time milestones and budget tracking</p>
            </div>
            <button 
              onClick={() => setActiveTab('projects')}
              className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
            >
              View All Boards <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {projects.map(proj => {
              const leadMember = team.find(m => m.id === proj.lead);
              return (
                <div 
                  key={proj.id}
                  onClick={() => {
                    setCurrentProjectId(proj.id);
                    setActiveTab('projects');
                  }}
                  className="bg-slate-50/70 hover:bg-slate-50 border border-slate-200/60 rounded-2xl p-5 transition cursor-pointer group space-y-4"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-[10px] font-mono font-bold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                        {proj.key}
                      </span>
                      <h3 className="text-base font-bold text-slate-900 mt-2 group-hover:text-indigo-600 transition">
                        {proj.name}
                      </h3>
                    </div>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">
                      {proj.status}
                    </span>
                  </div>

                  <p className="text-xs text-slate-600 line-clamp-2">{proj.description}</p>

                  <div className="space-y-1.5">
                    <div className="flex justify-between text-xs font-semibold">
                      <span className="text-slate-500">Progress</span>
                      <span className="text-slate-900">{proj.progress}%</span>
                    </div>
                    <div className="w-full bg-slate-200 h-2 rounded-full overflow-hidden">
                      <div className="bg-indigo-600 h-full rounded-full transition-all duration-500" style={{ width: `${proj.progress}%` }}></div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-slate-200/60 text-xs">
                    <div className="flex items-center space-x-2">
                      {leadMember && (
                        <img src={leadMember.avatar} alt={leadMember.name} className="w-6 h-6 rounded-full object-cover" />
                      )}
                      <span className="text-slate-600 font-medium">{leadMember?.name}</span>
                    </div>
                    <span className="font-bold text-slate-900">{proj.budget}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Urgent Attention / Action Items (1 col) */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Urgent Focus</h2>
              <p className="text-xs text-slate-500">High priority blocker items</p>
            </div>
            <span className="text-xs font-bold px-2 py-0.5 bg-rose-100 text-rose-700 rounded-full">
              {urgentTasks.length} Tasks
            </span>
          </div>

          <div className="space-y-3">
            {urgentTasks.length === 0 && (
              <p className="text-xs text-slate-400 italic py-6 text-center">No urgent blockers right now. Great job!</p>
            )}
            {urgentTasks.map(task => {
              const assignee = team.find(m => m.id === task.assigneeId);
              return (
                <div key={task.id} className="p-4 rounded-xl bg-rose-50/40 border border-rose-100/60 space-y-2">
                  <div className="flex items-start justify-between">
                    <h3 className="text-sm font-bold text-slate-900 line-clamp-1">{task.title}</h3>
                    <span className="text-[10px] font-bold px-2 py-0.5 bg-rose-200 text-rose-800 rounded">
                      {task.priority}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 line-clamp-2">{task.description}</p>
                  
                  <div className="flex items-center justify-between pt-2 text-xs">
                    <div className="flex items-center space-x-2">
                      {assignee && (
                        <img src={assignee.avatar} alt={assignee.name} className="w-5 h-5 rounded-full object-cover" />
                      )}
                      <span className="text-slate-600">{assignee?.name}</span>
                    </div>
                    <span className="font-mono text-slate-500 text-[11px]">Due {task.dueDate}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Quick AI Prompt Trigger */}
          <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100 space-y-3">
            <div className="flex items-center space-x-2 text-indigo-900 font-bold text-xs">
              <Bot className="w-4 h-4 text-indigo-600" />
              <span>Ask Nexus AI Copilot</span>
            </div>
            <p className="text-xs text-indigo-700">Need a risk assessment or burndown forecast? Launch AI Studio.</p>
            <button 
              onClick={() => setActiveTab('ai')}
              className="w-full py-2 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 transition shadow-sm"
            >
              Open AI Studio
            </button>
          </div>
        </div>

      </div>

    </div>
  );
}
